import React, { InputHTMLAttributes, useCallback, useImperativeHandle, useRef } from "react";
import cx from "classnames";
import Big from "big.js";

import styles from "./index.module.scss";

export interface XtInputNumberRefProps extends HTMLInputElement {
  increase: () => void;
  decrease: () => void;
  focus: () => void;
}

export interface XtInputNumberProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "onInput"> {
  disabled?: boolean;
  disabledLabel?: string;
  max?: number;
  min?: number;
  point?: number; //保留几位小数
  isStepPoint?: boolean; //单步是否是point
  onInput?: (val: string) => void;
}

const XtInputNumber = React.forwardRef<XtInputNumberRefProps, XtInputNumberProps>(
  ({ className, value, disabled = false, disabledLabel, max = Infinity, min = 0, point, isStepPoint, onInput, ...rest }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const $emit = useCallback(
      (val) => {
        onInput && onInput(val);
      },
      [onInput]
    );

    const handleInput = useCallback(
      (e) => {
        // console.log("handleInput===", e);
        // debugger;
        if (disabledLabel) return;
        const newVal = e.target.value;

        if (newVal === "") return $emit(newVal);
        //数字检测
        if (!/^[0-9]+\.?[0-9]*$/.test(newVal)) return;
        //如果0开头
        if (/^0/.test(newVal) && !/^0(\.|$)/.test(newVal)) return;
        //判断有效位数
        if (typeof point === "number" && /\./.test(newVal)) {
          if (newVal.split(".")[1].length > point) return;
        }
        //判断数值是否在有效区间内
        const numValue = Number(newVal);
        if (numValue > max) return $emit(max + "");
        if (numValue < min) return $emit(min + "");

        $emit(newVal);
      },
      [$emit, disabledLabel, point, max, min]
    );

    const change = useCallback(
      (num) => {
        let step: number | string = 1,
          fixLen;

        if (point && isStepPoint) {
          fixLen = point;
          step = "0." + "0".repeat(point - 1) + "1";
        } else {
          const decimal = ((value || "") + "").split(".")[1];
          if (decimal) {
            step = "0." + "0".repeat(decimal.length - 1) + "1";
            fixLen = decimal.length;
          }
        }

        const newVal = Big((value as number | string) || 0)
          .plus(Big(step).times(num))
          .toFixed(fixLen);

        $emit(newVal);
      },
      [point, isStepPoint, value, $emit]
    );
    const increase = useCallback(() => {
      if (disabled) return;
      if (+(value || 0) >= max) return;
      change(1);
    }, [disabled, value, max, change]);
    const decrease = useCallback(() => {
      if (disabled) return;
      if (+(value || 0) <= min) return;
      change(-1);
    }, [disabled, value, min, change]);

    const handleKeyDown = useCallback(
      (e) => {
        // console.log("handleKeyDown");
        e.stopPropagation();
        // e.preventDefault();
        const keyName = e.key;
        if (keyName === "ArrowUp") {
          increase();
          e.preventDefault();
        } else if (keyName === "ArrowDown") {
          decrease();
          e.preventDefault();
        }
      },
      [increase, decrease]
    );

    useImperativeHandle(
      ref,
      (): any => {
        return {
          increase,
          decrease,
          focus: () => {
            if (!inputRef.current) return;
            inputRef.current.focus();
            const value = inputRef.current.value;
            inputRef.current.value = "";
            inputRef.current.value = value;
          },
        };
      },
      [increase, decrease]
    );

    return (
      <input
        ref={inputRef}
        className={cx(styles.main, className)}
        inputMode={"decimal"}
        value={disabledLabel || value}
        disabled={disabled}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        {...rest}
      />
    );
  }
);

XtInputNumber.displayName = "XtInputNumber";

export default XtInputNumber;
